/**
 * Scene composition.
 *
 * Two interactive behaviors wired here:
 *
 *   1. Camera target tracks the selected node every frame.
 *      OrbitControls maintains `camera.position = target + sphericalOffset`,
 *      so changing `target` translates the camera to keep the same angle
 *      and distance — the selected node lands at screen center and stays
 *      there even as autorotate continues to orbit around it. The card
 *      (anchored to the node) and any subsequent rotation pivot the entire
 *      view around the clicked node.
 *
 *   2. Autorotate is paused while ANY node is hovered.
 *      Slow autorotate (0.28 rad/s) shifts nodes a few pixels per frame.
 *      With the cursor near the edge of the hover card, those pixels are
 *      enough to repeatedly trip the card's pointerLeave. Pausing while
 *      hovered makes the card stable to read.
 */
import { OrbitControls } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { buildGraph } from '../data/build-graph';
import { SelectedMarker } from './SelectedMarker';
import { FreeLookKeys } from './FreeLookKeys';
import type { Line } from '../types/gameplan';
import { clusterIdFor } from '../data/cluster';
import type { AppConfig, Counter, Graph, Move, Position } from '../types/domain';
import { useAppStore, type Selection } from '../state/store';
import { Nodes } from './Nodes';
import { Edges } from './Edges';
import { EdgeFlow } from './EdgeFlow';
import { EdgeLabel } from './EdgeLabel';
import { useForceLayout, pickCenter, type ForceLayout } from './useForceLayout';
import { CenterAnchor } from './CenterAnchor';
import { HubLabels } from './HubLabels';
import { ProximityLabels } from './ProximityLabels';
import { ClusterLabels } from './ClusterLabels';
import { Starfield } from '../zones/Starfield';

const TARGET_LERP = 0.18;

// When nothing is selected, bias the OrbitControls target slightly above
// the world origin (where Game Over is pinned). Since the camera looks at
// the target, this shifts the rendered scene DOWN on screen, leaving
// room at the top for the HUD chips and giving the constellation a
// better visual center. ~12% of the outer-shell radius.
const NEUTRAL_TARGET_Y = 4.5;
const NEUTRAL_TARGET = new THREE.Vector3(0, NEUTRAL_TARGET_Y, 0);

type Props = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  config: AppConfig;
  /** Pre-cluster reference sets used for centrality (degree/hub/
   *  orphan) — see App.tsx's FilteredSets doc. */
  referencePositions: Position[];
  referenceMoves: Move[];
};

/**
 * Compute the highlighted set: nodes and edge indices that should pulse with
 * a glow because they belong to the currently hovered/selected node's
 * 1-hop neighborhood.
 *
 * Rules:
 *  - hover always wins over selection (more responsive).
 *  - position/counter selection → primary = that node, glow primary + its
 *    adjacency neighbors, glow edges incident to primary.
 *  - move selection (no direct node in position-mode) → glow the edges
 *    belonging to that moveId and their endpoints.
 */
// Per-edge directional flag: 'out' = active node is the edge's source
// (move goes OUT from active), 'in' = active node is the edge's target
// (move comes IN to active), 'neutral' = no clear directional sense (e.g.
// a move preview, where the move IS the edge and no single node is focal).
// Edges.tsx uses these to tint the active end warm/cool.
export type EdgeDir = 'out' | 'in' | 'neutral';

// A move may not be the default of its sleeve — match by either the
// default `moveId` OR membership in the sleeve's `moveIds` list, so
// selecting/previewing a non-default sleeve member still lights up the
// correct sleeve edge.
function edgeCarriesMove(e: { moveId?: string; moveIds?: string[] }, moveId: string): boolean {
  if (e.moveId === moveId) return true;
  return e.moveIds ? e.moveIds.includes(moveId) : false;
}

function computeActive(
  graph: Graph,
  hoverId: string | null,
  selection: Selection,
  previewMoveId: string | null,
  showMovesFrom: boolean,
  showMovesTo: boolean,
): { glowNodes: Set<string>; glowEdges: Map<number, EdgeDir> } {
  const glowNodes = new Set<string>();
  const glowEdges = new Map<number, EdgeDir>();

  if (previewMoveId) {
    const asNode = graph.byId.get(previewMoveId);
    if (asNode) {
      glowNodes.add(previewMoveId);
      graph.edges.forEach((e, i) => {
        if (e.source === previewMoveId) {
          if (showMovesFrom) {
            glowEdges.set(i, 'out');
            glowNodes.add(e.target);
          }
        } else if (e.target === previewMoveId) {
          if (showMovesTo) {
            glowEdges.set(i, 'in');
            glowNodes.add(e.source);
          }
        }
      });
    } else {
      graph.edges.forEach((e, i) => {
        if (edgeCarriesMove(e, previewMoveId)) {
          glowEdges.set(i, 'neutral');
          glowNodes.add(e.source);
          glowNodes.add(e.target);
        }
      });
    }
    return { glowNodes, glowEdges };
  }

  // In MOVE-MODE the move IS a node, so when selection.kind === 'move'
  // and the id resolves to a node in the graph, we treat it as the
  // primary node — same code path as position-mode positions. The
  // edges-based glow lookup below is only valid in POSITION-MODE where
  // moves render as edges. Without this branch, clicking a move node
  // in move-mode showed the glow only while the cursor stayed over it:
  // as soon as hover cleared, the edge-glow fallback returned an empty
  // set (no edge "carries" a node-id in move-mode) and the highlight
  // disappeared.
  const selectionAsNode =
    selection.kind === 'move' && graph.byId.has(selection.id)
      ? selection.id
      : null;

  let primaryId: string | null = null;
  if (hoverId) primaryId = hoverId;
  else if (selection.kind === 'position') primaryId = selection.id;
  else if (selectionAsNode) primaryId = selectionAsNode;
  else if (selection.kind === 'counter')
    primaryId = `intercept:${selection.moveId}:${selection.id}`;

  // Position-mode move selection: the move is an EDGE, not a node.
  // Light up the sleeve edge and its endpoint nodes.
  if (!hoverId && selection.kind === 'move' && !selectionAsNode) {
    graph.edges.forEach((e, i) => {
      if (edgeCarriesMove(e, selection.id)) {
        glowEdges.set(i, 'neutral');
        glowNodes.add(e.source);
        glowNodes.add(e.target);
      }
    });
    return { glowNodes, glowEdges };
  }

  if (!primaryId) return { glowNodes, glowEdges };

  glowNodes.add(primaryId);
  // Per-direction neighborhood. With both flags off we still glow the
  // primary node by itself — useful for isolating one node in a dense
  // graph without the neighborhood halo.
  graph.edges.forEach((e, i) => {
    if (e.source === primaryId) {
      if (showMovesFrom) {
        glowEdges.set(i, 'out');
        glowNodes.add(e.target);
      }
    } else if (e.target === primaryId) {
      if (showMovesTo) {
        glowEdges.set(i, 'in');
        glowNodes.add(e.source);
      }
    }
  });
  return { glowNodes, glowEdges };
}

/**
 * Glow set for a gameplan line: the graph nodes/edges its referenced
 * positions, moves and counters map to. Position refs glow as nodes;
 * move refs glow as their sleeve edge (position-mode) or node
 * (move-mode); counter refs glow the interception node + its parent
 * move's edge. Returns null when nothing in the line resolves into the
 * current graph (e.g. all refs filtered out by another knob).
 */
function computeLineGlow(
  graph: Graph,
  line: Line,
): { glowNodes: Set<string>; glowEdges: Map<number, EdgeDir> } | null {
  const glowNodes = new Set<string>();
  const glowEdges = new Map<number, EdgeDir>();
  for (const node of line.nodes) {
    const ref = node.ref;
    if (ref.kind === 'position') {
      if (graph.byId.has(ref.id)) glowNodes.add(ref.id);
    } else if (ref.kind === 'move') {
      if (graph.byId.has(ref.id)) {
        // move-mode: the move is its own node.
        glowNodes.add(ref.id);
      } else {
        // position-mode: the move is a sleeve edge.
        graph.edges.forEach((e, i) => {
          if (edgeCarriesMove(e, ref.id)) {
            glowEdges.set(i, 'neutral');
            glowNodes.add(e.source);
            glowNodes.add(e.target);
          }
        });
      }
    } else {
      const interceptId = `intercept:${ref.moveId}:${ref.id}`;
      if (graph.byId.has(interceptId)) glowNodes.add(interceptId);
      graph.edges.forEach((e, i) => {
        if (edgeCarriesMove(e, ref.moveId)) {
          glowEdges.set(i, 'neutral');
          glowNodes.add(e.source);
          glowNodes.add(e.target);
        }
      });
    }
  }
  if (glowNodes.size === 0 && glowEdges.size === 0) return null;
  return { glowNodes, glowEdges };
}

export function Scene({
  positions,
  moves,
  counters,
  config,
  referencePositions,
  referenceMoves,
}: Props) {
  const mode = useAppStore((s) => s.mode);
  const selection = useAppStore((s) => s.selection);
  const hoverId = useAppStore((s) => s.hoverId);
  const previewMoveId = useAppStore((s) => s.previewMoveId);
  const showInterceptions = useAppStore((s) => s.showInterceptions);
  const clusterBy = useAppStore((s) => s.clusterBy);

  // Lookups for cluster derivation. Stable per loaded dataset.
  // Filtering (gi / cluster / hide) all happens in App.tsx's
  // applyFilters now so Scene and ListView consume the same set.
  const positionsById = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );
  const movesById = useMemo(() => new Map(moves.map((m) => [m.id, m])), [moves]);

  const graph = useMemo(
    () =>
      buildGraph(positions, moves, counters, mode, {
        showInterceptions,
        referencePositions,
        referenceMoves,
      }),
    [
      positions,
      moves,
      counters,
      mode,
      showInterceptions,
      referencePositions,
      referenceMoves,
    ],
  );
  const centerId = useMemo(() => pickCenter(graph, config), [graph, config]);
  const clusterStrength = useAppStore((s) => s.clusterStrength);
  const familySubClusters = useAppStore((s) => s.familySubClusters);
  const layout = useForceLayout(
    graph,
    config,
    clusterBy,
    positionsById,
    movesById,
    clusterStrength,
    familySubClusters,
  );

  const showMovesFrom = useAppStore((s) => s.showMovesFrom);
  const showMovesTo = useAppStore((s) => s.showMovesTo);

  const gameplanFilter = useAppStore((s) => s.gameplanFilter);
  const activeGameplanId = useAppStore((s) => s.activeGameplanId);
  const activeLineId = useAppStore((s) => s.activeLineId);
  const gameplans = useAppStore((s) => s.gameplans);

  // Compute the highlighted set every time hover/selection/graph changes.
  // Per-frame consumers (Nodes, Edges) read these Sets in useFrame.
  const { glowNodes, glowEdges } = useMemo(
    () => computeActive(graph, hoverId, selection, previewMoveId, showMovesFrom, showMovesTo),
    [graph, hoverId, selection, previewMoveId, showMovesFrom, showMovesTo],
  );

  // Gameplan line highlight. When a line is active in the gameplan view,
  // its path becomes the glow set. With the gameplan filter on we glow it
  // ADDITIVELY (additiveGlow) so the rest of the filtered-to-gameplan
  // graph stays lit rather than dimming — the line reads as an emphasis
  // within the plan instead of hiding everything else.
  const lineGlow = useMemo(() => {
    if (activeGameplanId === null || activeLineId === null) {
      return null;
    }
    const gp = gameplans.find((g) => g.id === activeGameplanId);
    const line = gp?.lines.find((l) => l.id === activeLineId);
    if (!line) return null;
    return computeLineGlow(graph, line);
  }, [activeGameplanId, activeLineId, gameplans, graph]);

  const gameplanFilterActive = gameplanFilter && activeGameplanId !== null;
  const effGlowNodes = lineGlow ? lineGlow.glowNodes : glowNodes;
  const effGlowEdges = lineGlow ? lineGlow.glowEdges : glowEdges;
  const additiveGlow = gameplanFilterActive && lineGlow !== null;

  // Node the selected-position marker pins to: the focal node of the
  // current selection (position, move-as-node, or counter interception).
  const selectedFocalId = useMemo(() => {
    if (selection.kind === 'position') return selection.id;
    if (selection.kind === 'move' && graph.byId.has(selection.id)) {
      return selection.id;
    }
    if (selection.kind === 'counter') {
      const id = `intercept:${selection.moveId}:${selection.id}`;
      return graph.byId.has(id) ? id : null;
    }
    return null;
  }, [selection, graph]);

  // Drawer / list MoveTable hover sets previewMoveId. To make the
  // sleeve containing that move fan out in the 3D scene (so the user
  // can see the specific sub-arc within its group), derive the sleeve
  // index from previewMoveId and push it into the same
  // hoveredSleeveIndex store slot that the in-scene edge hover uses.
  // The cleanup clears it back to null when the preview ends or
  // changes — keeps the hover gestures from each surface from leaking
  // into the others.
  const setHoveredSleeveIndex = useAppStore((s) => s.setHoveredSleeveIndex);
  useEffect(() => {
    if (!previewMoveId) return;
    const idx = graph.edges.findIndex(
      (e) =>
        e.kind === 'move' &&
        (e.moveId === previewMoveId ||
          (e.moveIds?.includes(previewMoveId) ?? false)),
    );
    if (idx < 0) return;
    setHoveredSleeveIndex(idx);
    return () => setHoveredSleeveIndex(null);
  }, [previewMoveId, graph.edges, setHoveredSleeveIndex]);

  // Always-labeled set (center + top-N hubs) — excluded from ProximityLabels
  // so each node has exactly one label source.
  const alwaysLabeled = useMemo(() => {
    const set = new Set<string>([centerId]);
    const limit = config.labelCount ?? 7;
    graph.nodes
      .filter((n) => n.id !== centerId && (n.kind === 'position' || n.kind === 'move') && n.isHub)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, limit)
      .forEach((n) => set.add(n.id));
    return set;
  }, [graph, centerId, config.labelCount]);

  return (
    <Suspense fallback={null}>
      <ambientLight intensity={0.7} />
      <directionalLight position={[10, 10, 10]} intensity={0.45} />

      <Starfield />

      <Edges
        graph={graph}
        layout={layout}
        glowEdges={effGlowEdges}
        additiveGlow={additiveGlow}
        forceEdges={gameplanFilterActive}
      />
      {/* Directional flow cones riding each move edge — see EdgeFlow. */}
      <EdgeFlow
        graph={graph}
        layout={layout}
        glowEdges={effGlowEdges}
        forceEdges={gameplanFilterActive}
      />
      <Nodes
        graph={graph}
        layout={layout}
        excludeId={centerId}
        glowNodes={effGlowNodes}
        additiveGlow={additiveGlow}
        clusterBy={clusterBy}
        positionsById={positionsById}
        movesById={movesById}
      />

      <CenterAnchor
        graph={graph}
        layout={layout}
        centerId={centerId}
        glowNodes={effGlowNodes}
      />
      <SelectedMarker
        graph={graph}
        layout={layout}
        focalId={selectedFocalId}
        centerId={centerId}
      />
      <HubLabels
        graph={graph}
        layout={layout}
        centerId={centerId}
        maxLabels={config.labelCount}
        glowNodes={effGlowNodes}
        clusterBy={clusterBy}
        positionsById={positionsById}
        movesById={movesById}
      />
      <ProximityLabels
        graph={graph}
        layout={layout}
        excludeIds={alwaysLabeled}
        glowNodes={effGlowNodes}
        clusterBy={clusterBy}
        positionsById={positionsById}
        movesById={movesById}
      />
      <ClusterLabels
        graph={graph}
        layout={layout}
        clusterBy={clusterBy}
        positionsById={positionsById}
        movesById={movesById}
        glowNodes={effGlowNodes}
        centerId={centerId}
      />

      {/* Names the hovered edge, pinned to that arc's midpoint. */}
      <EdgeLabel
        graph={graph}
        layout={layout}
        positions={positions}
        moves={moves}
        counters={counters}
      />

      <CameraDirector
        graph={graph}
        layout={layout}
        selection={selection}
        hoverId={hoverId}
        centerId={centerId}
        clusterBy={clusterBy}
        positionsById={positionsById}
        movesById={movesById}
      />

      {/* Onboarding coach — features a hub node during node steps:
          selects it (highlight + camera focus) and publishes its screen
          position so the DOM coach bubble can point straight at it. */}
      <CoachNodeAnchor graph={graph} layout={layout} centerId={centerId} />

      <EffectComposer multisampling={0}>
        <Bloom
          intensity={0.45}
          luminanceThreshold={0.55}
          luminanceSmoothing={0.6}
          mipmapBlur
        />
      </EffectComposer>
    </Suspense>
  );
}

/**
 * Single component that owns OrbitControls and keeps the camera target
 * lerped toward the selected node's live world position. Camera distance
 * is manual-only (OrbitControls' built-in scroll-zoom + drag); we don't
 * auto-dolly on selection.
 */
function CameraDirector({
  graph,
  layout,
  selection,
  hoverId,
  centerId,
  clusterBy,
  positionsById,
  movesById,
}: {
  graph: Graph;
  layout: ForceLayout;
  selection: Selection;
  hoverId: string | null;
  centerId: string;
  clusterBy: import('../types/domain').ClusterBy;
  positionsById: Map<string, Position>;
  movesById: Map<string, Move>;
}) {
  const controlsRef = useRef<OrbitControlsImpl>(null!);
  const desiredTarget = useRef(new THREE.Vector3());
  const focusedClusterId = useAppStore((s) => s.focusedClusterId);
  // Resolved each frame: members of the currently focused cluster, if any.
  const focusedClusterMembers = useMemo(() => {
    if (!focusedClusterId || clusterBy === 'none') return null;
    const idxList: number[] = [];
    for (const n of graph.nodes) {
      if (n.kind !== 'position' && n.kind !== 'move') continue;
      const cid = clusterIdFor(n, clusterBy, positionsById, movesById);
      if (cid === focusedClusterId) {
        const i = layout.nodeIndex.get(n.id);
        if (i !== undefined) idxList.push(i);
      }
    }
    return idxList.length > 0 ? idxList : null;
  }, [focusedClusterId, clusterBy, positionsById, movesById, graph, layout.nodeIndex]);

  // Resolve the world-space point the camera should look at, given the
  // current selection. Handles all five "interesting" selection cases:
  //   - none: fall back to NEUTRAL_TARGET (slight Y bias)
  //   - position node: that node's position
  //   - counter (interception node): that node's position
  //   - move node (move-mode): that node's position
  //   - move edge (position-mode, where the move is a sleeve edge, not
  //     a node): the chord midpoint of the source and target positions
  //     — i.e. the visual center of the edge — so clicking an edge
  //     re-centers the camera on the edge rather than dropping back to
  //     the neutral target.
  // Returns true on success, false if positions aren't yet available
  // (in which case the caller should leave desiredTarget unchanged).
  const resolveTarget = (out: THREE.Vector3): boolean => {
    const pos = layout.posRef.current;
    if (!pos) return false;
    // Cluster focus overrides selection — when the user clicks a cluster
    // key entry, lerp the camera onto that cluster's live centroid until
    // they clear it (or pick a node).
    if (focusedClusterMembers && focusedClusterMembers.length > 0) {
      let cx = 0, cy = 0, cz = 0;
      for (const i of focusedClusterMembers) {
        cx += pos[i * 3 + 0];
        cy += pos[i * 3 + 1];
        cz += pos[i * 3 + 2];
      }
      const inv = 1 / focusedClusterMembers.length;
      out.set(cx * inv, cy * inv, cz * inv);
      return true;
    }
    if (selection.kind === 'none') {
      out.copy(NEUTRAL_TARGET);
      return true;
    }
    if (selection.kind === 'position') {
      const idx = layout.nodeIndex.get(selection.id);
      if (idx === undefined) return false;
      out.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);
      return true;
    }
    if (selection.kind === 'counter') {
      const intId = `intercept:${selection.moveId}:${selection.id}`;
      const idx = layout.nodeIndex.get(intId);
      if (idx === undefined) return false;
      out.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);
      return true;
    }
    // selection.kind === 'move'
    const asNodeIdx = layout.nodeIndex.get(selection.id);
    if (asNodeIdx !== undefined) {
      // move-mode: the move IS a node
      out.set(pos[asNodeIdx * 3], pos[asNodeIdx * 3 + 1], pos[asNodeIdx * 3 + 2]);
      return true;
    }
    // position-mode: the move is an edge. Find the edge carrying this
    // moveId (could be the default move, or any member of a sleeve).
    const edge = graph.edges.find(
      (e) => e.moveId === selection.id || (e.moveIds?.includes(selection.id) ?? false),
    );
    if (!edge) return false;
    const srcIdx = layout.nodeIndex.get(edge.source);
    const tgtIdx = layout.nodeIndex.get(edge.target);
    if (srcIdx === undefined || tgtIdx === undefined) return false;
    out.set(
      (pos[srcIdx * 3] + pos[tgtIdx * 3]) * 0.5,
      (pos[srcIdx * 3 + 1] + pos[tgtIdx * 3 + 1]) * 0.5,
      (pos[srcIdx * 3 + 2] + pos[tgtIdx * 3 + 2]) * 0.5,
    );
    return true;
  };

  // On selection change, snap desiredTarget to the resolved point.
  useEffect(() => {
    if (!resolveTarget(desiredTarget.current)) {
      desiredTarget.current.copy(NEUTRAL_TARGET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, layout, graph]);

  // On mount, snap the live OrbitControls target to the neutral pose so
  // the first frame already shows the biased framing (otherwise it
  // would lerp from (0,0,0) for a few frames after load).
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.target.copy(NEUTRAL_TARGET);
    }
  }, []);

  // Re-snap when the layout swaps (e.g., mode toggle).
  useEffect(() => {
    // Defer one tick so the worker has emitted at least one positions frame.
    const t = setTimeout(() => {
      resolveTarget(desiredTarget.current);
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  const tourRunning = useAppStore((s) => s.tourRunning);

  const freeLook = useAppStore((s) => s.freeLook);

  useFrame(() => {
    if (!controlsRef.current) return;
    // FREE LOOK: leave the pivot alone.
    //
    // This lerp is what makes the camera feel tethered — pan away and the
    // target slides back onto the selection within a few frames, so the
    // view always drifts home. Skipping it is the whole mode: the pivot
    // stays where the user left it and pan/zoom behave like any other 3D
    // viewport. Nothing else needs to change.
    if (freeLook) return;
    // Keep desiredTarget tracking the live position of the selection
    // every frame so it stays current as the sim shifts nodes around.
    resolveTarget(desiredTarget.current);
    // ALWAYS lerp (incl. during the tour). An instant target.copy() here
    // fought OrbitControls' rotation damping and made the featured node —
    // and the coach card anchored to it — jitter while the user orbited.
    // The smooth lerp is the same code path normal node-selection uses,
    // which never shakes on rotate; pan is disabled during the tour (see
    // OrbitControls below) so the old pan-vs-lerp shake can't return either.
    controlsRef.current.target.lerp(desiredTarget.current, TARGET_LERP);
  });

  // Pause autorotate while hovering a node OR an edge OR while a
  // selection is active — all three states call for stable framing.
  // `previewMoveId` is set by edge-hover (and drawer row hover), so it
  // catches the edge case the node-hover check misses. Manual drag
  // still works (OrbitControls handles user input regardless of the
  // autorotate flag). Hard-off during the tour.
  const autorotatePaused = useAppStore((s) => s.autorotatePaused);
  const previewMoveId = useAppStore((s) => s.previewMoveId);
  // Autorotate spins the camera AROUND the orbit target. In free look the
  // target is wherever the user parked it, so orbiting it is disorienting
  // rather than ambient — off.
  const autoRotate =
    !freeLook &&
    !tourRunning &&
    !autorotatePaused &&
    hoverId === null &&
    previewMoveId === null &&
    selection.kind === 'none';

  // Reference centerId so the linter sees it; the value is conceptually
  // used as the default target via desiredTarget = (0,0,0) when nothing
  // is selected.
  void centerId;

  return (
    <>
    <FreeLookKeys controls={controlsRef} />
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableDamping
      dampingFactor={0.08}
      rotateSpeed={0.6}
      // Free look widens the range: the tethered view keeps the camera in
      // a band that frames one node well, which is exactly the constraint
      // you want gone when the point is to roam the whole constellation.
      minDistance={freeLook ? 1.5 : 4}
      maxDistance={freeLook ? 320 : 180}
      autoRotate={autoRotate}
      autoRotateSpeed={0.28}
      // During the tour we only kill AUTO-rotate (above) — the user can
      // still orbit by hand around the (smoothly-tracked) featured node.
      // Pan stays off so the old pan-vs-target-lerp shake can't return.
      enablePan={!tourRunning || freeLook}
      // Scroll-zoom is OrbitControls' own; it dollies toward the pivot,
      // which in free look is whatever the keys left in front of you.
      enableZoom
    />
    </>
  );
}

// The node the First Orbit tour features on its node steps.
//
// North-South is a position a newcomer can picture from the name alone,
// and it is the best-evidenced node on the map at 238 clips. What makes
// it the right one to open on is the SHAPE of its connections: 17 ways
// in and 1 way out. The fan of arrows all pointing inward is the map's
// whole argument in one picture — positions are somewhere you arrive
// from many places, and the tour's first node should look like that
// rather than like a junction.
//
// It replaced Knee on Belly (79 clips, 5 in / 2 out), which read as a
// smaller version of the same idea.
const PREFERRED_TUTORIAL_NODE = 'northsouth-top';

/**
 * Features a node during the onboarding tour. When the coach is on a node
 * step (`coachNodeActive`), this picks a stable, approachable node (Knee
 * on Belly, or the biggest hub as fallback) and SELECTS it — the existing
 * selection machinery highlights it and eases the camera onto it so it
 * sits at the centre of the view. The coach bubble then anchors to the
 * canvas centre (see OnboardingCoach.anchorRect), so no per-frame
 * projection is needed. On deactivation it drops the selection it made.
 */
function CoachNodeAnchor({
  graph,
  layout,
  centerId,
}: {
  graph: Graph;
  layout: ForceLayout;
  centerId: string;
}) {
  const coachNodeActive = useAppStore((s) => s.coachNodeActive);
  const setSelection = useAppStore((s) => s.setSelection);
  // Latches once we've featured the node for the current activation, so
  // we assert the selection exactly once (not every frame — that would
  // yank the user back if they click another node during the step).
  const assertedRef = useRef(false);

  // The featured node: PREFERRED_TUTORIAL_NODE when the current graph
  // contains it, otherwise the highest-weight hub.
  //
  // The fallback is for move-mode and for filtered views, where the
  // preferred node genuinely is not present. It is deliberately NOT the
  // thing that normally runs: "whatever is heaviest" lands on Side
  // Control (73 neighbours), which is the most overwhelming node on the
  // map to meet first. check-id-refs validates the preferred id against
  // the live map so a rename fails the build rather than quietly
  // demoting the tour to that fallback.
  const target = useMemo(() => {
    const preferred = graph.byId.get(PREFERRED_TUTORIAL_NODE);
    if (
      preferred &&
      (preferred.kind === 'position' || preferred.kind === 'move')
    ) {
      return { id: preferred.id, kind: preferred.kind };
    }
    let bestId: string | null = null;
    let bestW = -Infinity;
    let bestKind: 'position' | 'move' | null = null;
    for (const n of graph.nodes) {
      if (n.id === centerId) continue;
      if (n.kind !== 'position' && n.kind !== 'move') continue;
      if (n.weight > bestW) {
        bestW = n.weight;
        bestId = n.id;
        bestKind = n.kind;
      }
    }
    return bestId && bestKind ? { id: bestId, kind: bestKind } : null;
  }, [graph, centerId]);

  // On DEACTIVATION: re-arm the assert latch, and keep the selection.
  //
  // `coachNodeActive` going false is NOT the tour ending — it is the tour
  // moving on to the search / controls / settings steps. Dropping the
  // selection there made the map blink back to nothing mid-tour, which
  // read as losing your place. So the featured node stays selected while
  // the tour continues.
  useEffect(() => {
    if (coachNodeActive) return;
    assertedRef.current = false;
  }, [coachNodeActive]);

  /**
   * The tour ENDING is a different event, and it should hand the map back.
   *
   * Leaving a node selected after finish or skip means the tour keeps a
   * drawer open and a node highlighted that the user never chose — and on
   * skip especially, the whole point was to get out of the way.
   *
   * Only cleared if the selection is still the node WE asserted. If the
   * user clicked something else during the tour, that is their choice and
   * ending the tour must not take it away from them.
   */
  const tourRunningNow = useAppStore((s) => s.tourRunning);
  const wasRunning = useRef(false);
  useEffect(() => {
    if (tourRunningNow) { wasRunning.current = true; return; }
    if (!wasRunning.current) return;
    wasRunning.current = false;
    const sel = useAppStore.getState().selection;
    const stillOurs =
      target && sel.kind === target.kind && 'id' in sel && sel.id === target.id;
    if (stillOurs) setSelection({ kind: 'none' });
  }, [tourRunningNow, target, setSelection]);

  // Assert the featured-node selection ONCE, as soon as the layout is
  // ready (idx + positions resolved). Done in useFrame (not an effect) so
  // it's robust to the force layout not having emitted yet at tour start.
  // The camera then centres the node; the coach bubble anchors to the
  // canvas centre, so no per-frame projection is needed here.
  useFrame(() => {
    if (!coachNodeActive || !target || assertedRef.current) return;
    if (
      layout.nodeIndex.get(target.id) === undefined ||
      !layout.posRef.current
    ) {
      return;
    }
    const sel = useAppStore.getState().selection;
    const isSel =
      sel.kind === target.kind && 'id' in sel && sel.id === target.id;
    if (!isSel) {
      setSelection({ kind: target.kind, id: target.id }, { openDrawer: false });
    }
    assertedRef.current = true;
  });

  return null;
}
